import { cn } from '@virginexperiencedays/tailwind';

import {
  Collapsible,
  CollapsibleContent as BaseCollapsibleContent,
  CollapsibleTrigger,
  CollapsibleList,
  CollapsibleListExpandable,
  CollapsibleListTrigger,
  useCollapsibleList,
  type CollapsibleProps,
  type CollapsibleTriggerProps,
} from '@virginexperiencedays/components-v2/src/CollapsibleContent';
import {
  IconList,
  IconListIcon,
  IconListContent,
  IconListItem,
} from '@virginexperiencedays/components-v2/src/lists/IconList';
import { IconThumbsUp } from '@virginexperiencedays/components-v2/src/icons/IconThumbsUp';
import { HtmlContent } from '@virginexperiencedays/components-v2/src/pages/product/HtmlContent';

import type { HTMLAttributes } from 'react';

export type CollapsibleContentProps = HTMLAttributes<HTMLDivElement> & {
  lineClampCount?: CollapsibleProps['lineClampCount'];
  overrideMinHeight?: CollapsibleProps['overrideMinHeight'];
  onExpandCollapse?: CollapsibleTriggerProps['onClick'];
};

export const CollapsibleContent = ({
  children,
  className,
  lineClampCount = 3,
  onExpandCollapse,
  ...rest
}: CollapsibleContentProps) => (
  <Collapsible
    lineClampCount={lineClampCount}
    className={cn('flex flex-col items-start gap-2', className)}
    {...rest}
  >
    <BaseCollapsibleContent>{children}</BaseCollapsibleContent>
    <CollapsibleTrigger onClick={onExpandCollapse} />
  </Collapsible>
);

export const CollapsibleHtmlContent = ({
  children,
  content,
  ...rest
}: Omit<CollapsibleContentProps, 'content'> & { content: string }) => (
  <CollapsibleContent {...rest}>
    <HtmlContent content={content} />
    {children}
  </CollapsibleContent>
);

export const CollapsibleListContent = ({
  content,
  lineClampCount = 5,
  listLabel,
  onExpandCollapse,
  ...rest
}: Omit<CollapsibleContentProps, 'content'> & { content: string[]; listLabel?: string }) => {
  const {
    containerRef,
    expandableContentRef,
    expandedHeight,
    isExpandable,
    isExpanded,
    handleToggleExpanded,
    expandedList,
    truncatedList,
  } = useCollapsibleList<string>({ list: content, lineClampCount });

  const displayText = isExpanded ? 'Read Less' : 'Read More';

  const onCollapsibleListClick = () => {
    onExpandCollapse?.({ isExpanded, text: displayText });
  };

  if (!content?.length) return null;

  return (
    <CollapsibleList ref={containerRef} onClick={onCollapsibleListClick} {...rest}>
      <IconList className={isExpandable && 'pb-6'} aria-labelledby={listLabel}>
        {truncatedList.map((text) => (
          <IconListItem key={text}>
            <IconListIcon>
              <IconThumbsUp className="mt-0.5 h-5 w-5" width="20" height="20" />
            </IconListIcon>
            <IconListContent>{text}</IconListContent>
          </IconListItem>
        ))}
      </IconList>
      <CollapsibleListExpandable
        ref={expandableContentRef}
        isExpanded={isExpanded}
        expandedHeight={expandedHeight}
      >
        <IconList className="pb-6" aria-labelledby={listLabel} aria-expanded={isExpanded}>
          {expandedList.map((text) => (
            <IconListItem key={text}>
              <IconListIcon>
                <IconThumbsUp className="mt-0.5 h-5 w-5" width="20" height="20" />
              </IconListIcon>
              <IconListContent>{text}</IconListContent>
            </IconListItem>
          ))}
        </IconList>
      </CollapsibleListExpandable>
      <CollapsibleListTrigger
        isExpanded={isExpanded}
        isExpandable={isExpandable}
        handleToggleExpanded={handleToggleExpanded}
        className="self-start"
      >
        {displayText}
      </CollapsibleListTrigger>
    </CollapsibleList>
  );
};
